import get from "lodash/get"
import set from "lodash/set"
import unset from "lodash/unset"
import { useState } from "react"
import {
  FaChevronDown,
  FaChevronRight,
  FaTrash,
  FaPlus,
  FaRegImage,
  FaSpinner,
} from "react-icons/fa"
import { END_POINT, UploadSiteFile } from "../Redux/AdminReducer/action"
import { useDispatch } from "react-redux"
import CameraCapture from "../Components/CommonComponents/CameraCapture"

const createEmptyItem = (example) => {
  if (typeof example === "string") return ""
  if (Array.isArray(example)) return []
  if (typeof example === "object" && example !== null) {
    return Object.fromEntries(Object.keys(example).map((k) => [k, ""]))
  }
  return ""
}

const joinPath = (base, next) => {
  if (typeof next === "number") return `${base}[${next}]`
  if (!base) return next
  return `${base}.${next}`
}

const isImageFile = (value) => {
  if (typeof value !== "string") return false
  return /\.(png|jpe?g|webp|gif|svg)$/i.test(value)
}

const IMAGE_KEY =
  /(^|[._-])(img|image|logo|icon|photo|picture|avatar|banner|thumbnail|thumb|cover|poster|background|bg)s?([._-]|$)/i

const NON_IMAGE_KEY = /(alt|title|caption|desc|text|label|link|href|url|color|colour|position|size|width|height|type|count)/i

const isExternalUrl = (value) =>
  typeof value === "string" && /^(https?:)?\/\/|^data:image\//i.test(value.trim())

const isImageField = (key, value) => {
  if (typeof value !== "string") return false
  if (isImageFile(value.split("?")[0])) return true
  return IMAGE_KEY.test(String(key)) && !NON_IMAGE_KEY.test(String(key))
}

// "heroTitle" / "hero_title" -> "Hero Title"
const prettyLabel = (key) =>
  String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())

// Long-form copy gets a textarea instead of a single-line input.
const isLongText = (key, value) =>
  String(value).length > 70 ||
  /desc|content|body|about|summary|paragraph|message|text|bio|address/i.test(String(key))

// A short preview of an array item, so collapsed cards are identifiable.
const itemPreview = (item) => {
  if (typeof item === "string" || typeof item === "number") return String(item)
  if (Array.isArray(item)) return `${item.length} item${item.length === 1 ? "" : "s"}`
  if (typeof item === "object" && item !== null) {
    const hit = Object.entries(item).find(
      ([k, v]) => typeof v === "string" && v.trim() && !isImageFile(v) && /title|name|heading|label/i.test(k)
    )
    if (hit) return hit[1]
    const firstText = Object.values(item).find(
      (v) => typeof v === "string" && v.trim() && !isImageFile(v)
    )
    if (firstText) return firstText
  }
  return ""
}

const flattenLeaves = (value, prefix = "") => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Object.entries(value).flatMap(([k, v]) =>
      flattenLeaves(v, prefix ? `${prefix}.${k}` : k)
    )
  }
  return [[prefix, value]]
}

const emptyLike = (value) => {
  if (typeof value === "number") return 0
  if (typeof value === "boolean") return false
  if (Array.isArray(value)) return []
  return ""
}

const applyUpdates = (setPages, setModifiedData, updates) => {
  if (!updates.length) return
  setPages((prev) => {
    const copy = structuredClone(prev)
    updates.forEach(([p, v]) => {
      if (v === null) unset(copy, p)
      else set(copy, p, v)
    })
    return copy
  })
  setModifiedData((prev) => {
    const next = { ...prev }
    updates.forEach(([p, v]) => {
      next[p] = v
    })
    return next
  })
}

const updateByPath = (setPages, path, value, setModifiedData) => {
  setPages((prev) => {
    const copy = structuredClone(prev)
    set(copy, path, value)
    return copy
  })
  setModifiedData((prev) => ({ ...prev, [path]: value }))
}

/* ---------------------------------------------------------------- */
/* Image field                                                        */
/* ---------------------------------------------------------------- */

const ImageField = ({ data, path, setPages, setModifiedData }) => {
  const [imgKey, setImgKey] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [broken, setBroken] = useState(false)
  const dispatch = useDispatch()

  const external = isExternalUrl(data)

  const imageUrl = external
    ? data
    : `${END_POINT}/site_config/images/v2/${data}?v=${imgKey}`

  const hasImage = Boolean(data)

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const currentName = external ? "" : data
      const res = await dispatch(UploadSiteFile(file, "tenant_name", "images", currentName))
      const filename = res?.filename || currentName
      if (filename) {
        updateByPath(setPages, path, filename, setModifiedData)
        setBroken(false)
        setImgKey((k) => k + 1)
      }
    } finally {
      setUploading(false)
      e.target.value = ""
    }
  }

  return (
    <div className="group relative w-full max-w-sm overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
      <div className="flex h-40 items-center justify-center bg-[linear-gradient(45deg,#f1f5f9_25%,transparent_25%),linear-gradient(-45deg,#f1f5f9_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#f1f5f9_75%),linear-gradient(-45deg,transparent_75%,#f1f5f9_75%)] bg-[length:16px_16px] bg-[position:0_0,0_8px,8px_-8px,-8px_0]">
        {!hasImage || broken ? (
          <div className="flex flex-col items-center gap-1 text-slate-400">
            <FaRegImage className="h-7 w-7" />
            <span className="text-[11px]">
              {hasImage ? "Preview unavailable" : "No image yet"}
            </span>
          </div>
        ) : (
          <img
            key={imgKey}
            src={imageUrl}
            alt={prettyLabel(path.split(".").pop())}
            onError={() => setBroken(true)}
            className="max-h-40 w-full object-contain"
          />
        )}
      </div>

      {/* Hover / focus overlay */}
      <label
        className={`absolute inset-0 flex cursor-pointer items-center justify-center bg-slate-900/60 text-xs font-semibold text-white transition focus-within:opacity-100 group-hover:opacity-100 ${
          uploading || !hasImage ? "opacity-100" : "opacity-0"
        }`}
      >
        {uploading ? (
          <span className="inline-flex items-center gap-2">
            <FaSpinner className="h-3.5 w-3.5 animate-spin" /> Uploading…
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-1.5 backdrop-blur">
            <FaRegImage className="h-3.5 w-3.5" /> {hasImage ? "Replace image" : "Upload image"}
          </span>
        )}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/jpg,image/gif,image/svg+xml"
          className="sr-only"
          disabled={uploading}
          onChange={handleFile}
        />
      </label>

      <div className="flex items-center gap-2 border-t border-slate-200 bg-white px-3 py-1.5 font-mono text-[11px] text-slate-500">
        {external && (
          <span className="shrink-0 rounded bg-amber-100 px-1.5 font-sans text-[10px] font-semibold text-amber-700">
            external
          </span>
        )}
        <span className="truncate">{data || "—"}</span>
        <CameraCapture
          disabled={uploading}
          className="ml-auto shrink-0 !px-2 !py-0.5 font-sans !text-[11px]"
          onCapture={(file) => handleFile({ target: { files: [file], value: "" } })}
        />
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- */
/* Recursive field renderer                                           */
/* ---------------------------------------------------------------- */

const RenderField = ({ setModifiedData, data, path = "", setPages, level = 0 }) => {
  const [openIndex, setOpenIndex] = useState([0])

  const toggleIndex = (index) =>
    setOpenIndex((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    )

  /* ---------- scalar ---------- */
  if (typeof data === "string" || typeof data === "number") {
    if (typeof data === "string" && isImageField(path.split(".").pop(), data)) {
      return (
        <ImageField
          data={data}
          path={path}
          setPages={setPages}
          setModifiedData={setModifiedData}
        />
      )
    }

    const key = path.split(".").pop()
    const inputClass =
      "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 shadow-sm transition hover:border-slate-300 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"

    if (typeof data === "string" && isLongText(key, data)) {
      return (
        <textarea
          rows={Math.min(8, Math.max(3, Math.ceil(data.length / 70) + 1))}
          className={`${inputClass} resize-y leading-relaxed`}
          value={data}
          placeholder="Empty"
          onChange={(e) => updateByPath(setPages, path, e.target.value, setModifiedData)}
        />
      )
    }

    return (
      <input
        type={typeof data === "number" ? "number" : "text"}
        className={inputClass}
        value={data}
        placeholder="Empty"
        onChange={(e) =>
          updateByPath(
            setPages,
            path,
            typeof data === "number" && e.target.value !== ""
              ? Number(e.target.value)
              : e.target.value,
            setModifiedData
          )
        }
      />
    )
  }

  /* ---------- boolean ---------- */
  if (typeof data === "boolean") {
    return (
      <label className="inline-flex cursor-pointer items-center gap-3">
        <span className="relative inline-block">
          <input
            type="checkbox"
            checked={data}
            className="peer sr-only"
            onChange={(e) =>
              updateByPath(setPages, path, e.target.checked, setModifiedData)
            }
          />
          <span className="block h-6 w-11 rounded-full bg-slate-300 transition peer-checked:bg-sky-600 peer-focus-visible:ring-2 peer-focus-visible:ring-sky-500/40 peer-focus-visible:ring-offset-2" />
          <span className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition peer-checked:translate-x-5" />
        </span>
        <span className="text-sm font-medium text-slate-600">
          {data ? "Enabled" : "Disabled"}
        </span>
      </label>
    )
  }

  /* ---------- array ---------- */
  if (Array.isArray(data)) {
    const addItem = () =>
      updateByPath(
        setPages,
        path,
        [...data, createEmptyItem(data[0])],
        setModifiedData
      )

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-slate-400">
            {data.length} item{data.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            onClick={addItem}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 transition hover:bg-sky-100"
          >
            <FaPlus className="h-2.5 w-2.5" /> Add item
          </button>
        </div>

        {data.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-400">
            No items yet.
          </p>
        ) : (
          <div className="space-y-2">
            {data.map((item, index) => {
              const isOpen = openIndex.includes(index)
              const preview = itemPreview(item)

              return (
                <div
                  key={index}
                  className={`overflow-hidden rounded-xl border bg-white transition ${
                    isOpen ? "border-sky-300 shadow-sm" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleIndex(index)}
                      className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                    >
                      {isOpen ? (
                        <FaChevronDown className="h-3 w-3 shrink-0 text-sky-600" />
                      ) : (
                        <FaChevronRight className="h-3 w-3 shrink-0 text-slate-400" />
                      )}
                      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                        {index + 1}
                      </span>
                      <span className="truncate text-xs font-medium text-slate-600">
                        {preview || <span className="text-slate-300">Untitled item</span>}
                      </span>
                    </button>

                    <button
                      type="button"
                      title="Remove item"
                      className="shrink-0 cursor-pointer rounded-md p-1.5 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600"
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpenIndex((prev) =>
                          prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
                        )
                        updateByPath(
                          setPages,
                          path,
                          data.filter((_, i) => i !== index),
                          setModifiedData
                        )
                      }}
                    >
                      <FaTrash className="h-3 w-3" />
                    </button>
                  </div>

                  {isOpen && (
                    <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-3">
                      <RenderField
                        setModifiedData={setModifiedData}
                        data={item}
                        path={joinPath(path, index)}
                        setPages={setPages}
                        level={level + 1}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  /* ---------- object ---------- */
  if (typeof data === "object" && data !== null) {
    const allKeys = Object.keys(data)
    const isIndexed = allKeys.length > 0 && allKeys.every((k) => /^\d+$/.test(k))

    if (isIndexed) {
      const order = allKeys.map(Number).sort((a, b) => a - b)
      const template = data[String(order[0])]

      const addItem = () => {
        const next = order[order.length - 1] + 1
        const updates = flattenLeaves(template).map(([leaf, value]) => [
          leaf ? `${path}.${next}.${leaf}` : `${path}.${next}`,
          emptyLike(value),
        ])
        applyUpdates(setPages, setModifiedData, updates)
        setOpenIndex((prev) => [...prev, next])
      }

      const removeItem = (index) => {
        const updates = []
        for (let i = index; i < order.length - 1; i++) {
          const from = data[String(order[i + 1])]
          flattenLeaves(from).forEach(([leaf, value]) => {
            updates.push([leaf ? `${path}.${order[i]}.${leaf}` : `${path}.${order[i]}`, value])
          })
        }
        const last = data[String(order[order.length - 1])]
        flattenLeaves(last).forEach(([leaf]) => {
          updates.push([
            leaf ? `${path}.${order[order.length - 1]}.${leaf}` : `${path}.${order[order.length - 1]}`,
            null,
          ])
        })
        applyUpdates(setPages, setModifiedData, updates)
        setOpenIndex((prev) => prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i)))
      }

      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-slate-400">
              {order.length} item{order.length === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              onClick={addItem}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 transition hover:bg-sky-100"
            >
              <FaPlus className="h-2.5 w-2.5" /> Add item
            </button>
          </div>

          <div className="space-y-2">
            {order.map((idx, position) => {
              const item = data[String(idx)]
              const isOpen = openIndex.includes(idx)
              const preview = itemPreview(item)

              return (
                <div
                  key={idx}
                  className={`overflow-hidden rounded-xl border bg-white transition ${
                    isOpen ? "border-sky-300 shadow-sm" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleIndex(idx)}
                      className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                    >
                      {isOpen ? (
                        <FaChevronDown className="h-3 w-3 shrink-0 text-sky-600" />
                      ) : (
                        <FaChevronRight className="h-3 w-3 shrink-0 text-slate-400" />
                      )}
                      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                        {position + 1}
                      </span>
                      <span className="truncate text-xs font-medium text-slate-600">
                        {preview || <span className="text-slate-300">Untitled item</span>}
                      </span>
                    </button>

                    <button
                      type="button"
                      title="Remove item"
                      className="shrink-0 cursor-pointer rounded-md p-1.5 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600"
                      onClick={(e) => {
                        e.stopPropagation()
                        removeItem(position)
                      }}
                    >
                      <FaTrash className="h-3 w-3" />
                    </button>
                  </div>

                  {isOpen && (
                    <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-3">
                      <RenderField
                        setModifiedData={setModifiedData}
                        data={item}
                        path={joinPath(path, String(idx))}
                        setPages={setPages}
                        level={level + 1}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )
    }

    const entries = Object.entries(data)

    if (entries.length === 0) {
      return (
        <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-xs text-slate-400">
          Nothing to edit here.
        </p>
      )
    }

    return (
      <div className={level === 0 ? "space-y-6" : "space-y-4"}>
        {entries.map(([key, value]) => {
          const isNested = value !== null && typeof value === "object"

          return (
            <div
              key={key}
              className={
                isNested && level === 0
                  ? "rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                  : ""
              }
            >
              <div className="mb-2 flex items-baseline gap-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {prettyLabel(key)}
                </label>
                {Array.isArray(value) && (
                  <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-500">
                    list
                  </span>
                )}
              </div>

              <div className={isNested && level > 0 ? "border-l-2 border-slate-100 pl-3" : ""}>
                <RenderField
                  setModifiedData={setModifiedData}
                  data={value}
                  path={joinPath(path, key)}
                  setPages={setPages}
                  level={level + 1}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return null
}

/* ---------------------------------------------------------------- */

const AutoSectionEditor = ({ sectionKey, pages, setPages, setModifiedData }) => {
  const sectionData = get(pages, sectionKey)

  if (sectionData === undefined || sectionData === null) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
        <p className="text-sm font-medium text-slate-500">Select a section to start editing.</p>
      </div>
    )
  }

  return (
    <RenderField
      setModifiedData={setModifiedData}
      data={sectionData}
      path={sectionKey}
      setPages={setPages}
    />
  )
}

export default AutoSectionEditor
